import React from 'react';
import { Company, PolicyType } from '../../types';
import { cleanPolicyTitle } from '../../utils/policyUtils';
import { ShieldCheck, FileText, ArrowRight, ExternalLink } from 'lucide-react';

interface CompanyCardProps {
  company: Company;
  onSelect: (companyId: string, policyType: PolicyType) => void;
  onJobCreated?: (job: any) => void;
}

export const CompanyCard: React.FC<CompanyCardProps> = ({ company, onSelect, onJobCreated }) => {
  const getInitials = (name: string) => name.substring(0, 2).toUpperCase();

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500';
      case 'updating':
        return 'bg-blue-500 animate-pulse';
      case 'error':
        return 'bg-rose-500';
      default:
        return 'bg-emerald-500';
    }
  };

  const formatRelativeTime = (isoString?: string | null) => {
    if (!isoString) return 'Tracked';
    const date = new Date(isoString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  const logo = company.logoUrl || company.logo_url;
  const trackedPolicies = company.trackedPolicies || company.tracked_policies || {};
  const tosConfig = trackedPolicies['terms_of_service'];
  const privacyConfig = trackedPolicies['privacy_policy'];
  const totalRevs = company.totalRevisions || company.total_revisions || 1;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden group">
      {/* Top Card Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            {/* Clickable Logo */}
            <button
              onClick={() => onSelect(company.id, 'terms_of_service')}
              className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg transition-transform hover:scale-105"
              title={`View ${company.name} policies`}
            >
              {logo ? (
                <img
                  src={logo}
                  alt={`${company.name} logo`}
                  className="w-10 h-10 rounded-lg object-contain bg-slate-100 dark:bg-slate-700 p-1 border border-slate-200 dark:border-slate-600"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const next = e.currentTarget.nextElementSibling;
                    if (next) next.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white bg-gradient-to-br from-indigo-500 to-purple-600 shadow-inner ${
                  logo ? 'hidden' : ''
                }`}
              >
                {getInitials(company.name)}
              </div>
            </button>

            <div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onSelect(company.id, 'terms_of_service')}
                  className="font-semibold text-lg text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 text-left transition-colors cursor-pointer"
                  title={`View ${company.name} policies`}
                >
                  {company.name}
                </button>
                <span className="relative flex h-2.5 w-2.5" title={`Status: ${company.status || 'active'}`}>
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${getStatusColor(company.status)}`} />
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${getStatusColor(company.status)}`} />
                </span>
              </div>
              <a
                href={`https://${company.domain}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 inline-flex items-center gap-1"
              >
                {company.domain}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700/80 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
            {company.category}
          </span>
        </div>

        {company.description && (
          <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2 mb-4 leading-relaxed">
            {company.description}
          </p>
        )}

        {/* Tracked Policies Badges (Clean & Compact) */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            <span>Core Policies</span>
            <span className="text-[10px] bg-slate-100 dark:bg-slate-700/80 px-2 py-0.5 rounded-full lowercase font-medium">
              {Object.keys(trackedPolicies).length} tracked
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {(() => {
              const allEntries = Object.entries(trackedPolicies);
              // Prioritize terms_of_service and privacy_policy first
              const sorted = [...allEntries].sort(([aId], [bId]) => {
                if (aId === 'terms_of_service' || aId === 'privacy_policy') return -1;
                if (bId === 'terms_of_service' || bId === 'privacy_policy') return 1;
                return 0;
              });

              const primary = sorted.slice(0, 3);
              const remainingCount = sorted.length - primary.length;

              return (
                <>
                  {primary.map(([id, cfg]: [string, any]) => {
                    const cleanName = cleanPolicyTitle(cfg?.name, id);
                    const isPrivacy = id.includes('privacy');
                    return (
                      <button
                        key={id}
                        onClick={() => onSelect(company.id, id as PolicyType)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                          isPrivacy
                            ? 'bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300 border border-teal-200/80 dark:border-teal-800/80 hover:bg-teal-100 dark:hover:bg-teal-900/60'
                            : 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800/80 hover:bg-indigo-100 dark:hover:bg-indigo-900/60'
                        }`}
                      >
                        {isPrivacy ? <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" /> : <FileText className="w-3.5 h-3.5 flex-shrink-0" />}
                        <span className="truncate max-w-[160px]">{cleanName}</span>
                      </button>
                    );
                  })}

                  {remainingCount > 0 && (
                    <button
                      onClick={() => onSelect(company.id, sorted[3][0] as PolicyType)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                      title="View full suite of sub-agreements"
                    >
                      <span>+{remainingCount} more</span>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Recency & Revision Meta */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700/60 text-xs text-slate-500 dark:text-slate-400">
          <span>Updated {formatRelativeTime(company.updatedAt || company.updated_at || company.lastScrapedAt || company.last_scraped_at)}</span>
          <span className="text-[11px] text-slate-400 font-mono">
            {totalRevs} revision{totalRevs === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Card Action Footer */}
      <div className="bg-slate-50 dark:bg-slate-800/80 px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {Object.keys(trackedPolicies).length} policies monitored
        </span>

        <button
          onClick={() => onSelect(company.id, 'terms_of_service')}
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors cursor-pointer"
        >
          View Timeline & Diffs
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
